/**
 * <systems-board>: keeps the Systems page current. On each live snapshot it fetches the board as the server
 * renders it and morphs it into place (AnJS morph patches only what changed), flashing figures that moved. The
 * status pill's "Updated … ago" keeps time between snapshots.
 */

// Dependencies
import $, { morph, registerComponent } from 'almostnojs';
import { Enhanced } from './enhanced.js';
import { subscribe, updatedAgo } from '../js/live.js';

// A snapshot younger than this shows as live (matches the server).
const FRESH_MS = 10 * 60 * 1000;

/**
 * The Systems board.
 */
class SystemsBoard extends Enhanced {

    /**
     * Start the clock and listen for snapshots.
     *
     * @returns {void}
     */
    init() {

        // The status pill lives in the page hero, above the board.
        this.status = document.querySelector('[data-status]');
        this.time = this.status?.querySelector('[data-ago]');
        this.receivedAt = this.time?.getAttribute('datetime') ?? null;

        // Keep "Updated … ago" honest between snapshots.
        this.tick();
        const timer = setInterval(() => this.tick(), 5000);
        this.own(() => clearInterval(timer));

        // New snapshot: refresh the board.
        this.own(subscribe((live) => this.refresh(live)));
    }

    /**
     * Refresh the status pill's age and freshness.
     *
     * @returns {void}
     */
    tick() {

        // Nothing received yet.
        if (!this.time || !this.receivedAt) return;

        // Age and freshness.
        this.time.textContent = updatedAgo(this.receivedAt);
        this.time.setAttribute('datetime', this.receivedAt);
        this.status.classList.toggle('is-fresh', Date.now() - Date.parse(this.receivedAt) < FRESH_MS);
    }

    /**
     * Morph the board to the server's latest rendering when the snapshot is new. (Named refresh, not update:
     * update() is AnJSElement's own render step.)
     *
     * @param {Object} live - { available, receivedAt }.
     * @returns {Promise<void>}
     */
    async refresh(live) {

        // Same snapshot as on screen.
        if (!live?.available || live.receivedAt === this.receivedAt) return;

        // The board as the server renders it now.
        let markup;
        try {
            markup = await $.get('/systems/board', { timeout: 8000 });
        } catch {
            return;
        }

        // Figures before, patch, then flash the ones that changed.
        const before = new Map([...this.querySelectorAll('.tile')].map((tile) => [tile.dataset.k, tile.querySelector('.v')?.textContent]));
        morph(this, markup.trim());
        this.querySelectorAll('.tile').forEach((tile) => {
            const was = before.get(tile.dataset.k);
            if (was === undefined || was === tile.querySelector('.v')?.textContent) return;
            tile.classList.remove('changed');
            void tile.offsetWidth;
            tile.classList.add('changed');
        });

        // New age.
        this.receivedAt = live.receivedAt;
        this.tick();
    }
}

// Register
registerComponent('systems-board', SystemsBoard);
